import { ExtractionQuestion, QuestionType } from './extraction-question.model';

/**
 * Validates a complete extraction questionnaire schema based on our strict domain rules:
 * 1. Question text must not be blank.
 * 2. Choice questions (SINGLE_CHOICE, MULTI_CHOICE) must contain at least one non-blank option.
 * 3. Non-choice questions (FULL_TEXT, NUMERIC, BOOLEAN) must not contain options.
 *
 * @param questions The list of questions to validate
 * @returns Array of validation error messages. Empty if valid.
 */
export function validateQuestionSchema(questions: ExtractionQuestion[]): string[] {
  const errors: string[] = [];

  if (!questions || questions.length === 0) {
    return errors;
  }

  questions.forEach((q, index) => {
    const questionNum = index + 1;

    // 1. Text validation
    if (!q.text || q.text.trim() === '') {
      errors.push(`Question ${questionNum}: Text must not be empty.`);
    }

    const isChoiceType =
      q.type === QuestionType.SINGLE_CHOICE || q.type === QuestionType.MULTI_CHOICE;

    // 2. Choice validation
    if (isChoiceType) {
      if (!q.options || q.options.length === 0) {
        errors.push(
          `Question ${questionNum} ("${q.text || ''}"): Choice questions must specify at least one option.`,
        );
      } else {
        const activeOptions = q.options.filter((opt) => opt && opt.trim() !== '');
        if (activeOptions.length === 0) {
          errors.push(
            `Question ${questionNum} ("${q.text || ''}"): Choice questions must specify at least one option.`,
          );
        }
      }
    }

    // 3. Non-choice validation
    if (!isChoiceType && q.options && q.options.length > 0) {
      const activeOptions = q.options.filter((opt) => opt && opt.trim() !== '');
      if (activeOptions.length > 0) {
        errors.push(
          `Question ${questionNum} ("${q.text || ''}"): Non-choice questions cannot contain options.`,
        );
      }
    }
  });

  return errors;
}

/**
 * Pure helper function to handle drag-and-drop reordering. Returns a new array
 * with correct and updated orderIndex parameters.
 *
 * @param questions The current list of questions
 * @param fromIndex Original index of dragged question
 * @param toIndex Destination index of dropped question
 * @returns A new array of ExtractionQuestions with updated orderIndex properties
 */
export function reorderQuestions(
  questions: ExtractionQuestion[],
  fromIndex: number,
  toIndex: number,
): ExtractionQuestion[] {
  if (
    fromIndex < 0 ||
    fromIndex >= questions.length ||
    toIndex < 0 ||
    toIndex >= questions.length
  ) {
    return [...questions];
  }

  const result = [...questions];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);

  // Map and update orderIndex sequentially based on array index
  return result.map((q, idx) => ({
    ...q,
    orderIndex: idx,
  }));
}
